import { useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback } from "react";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { useAuth } from "~/contexts/AuthContext";
import { billingSettingsQueries } from "~/modules/billing/api/billingSettings/billingSettingsQueries.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { USERS_ACTIVE_STAFFINGS_QUERY_KEY } from "~/modules/project/api/staffing/staffingQueries.ts";
import CreateOrderForm from "~/modules/project/components/forms/CreateOrderForm";

type CreateOrderSidebarProps = {
	isOpen: boolean;
	setOpen: (open: boolean) => void;
	clientId: string;
	projectId: string;
	projectPhaseId: string;
};

const CreateOrderSidebar: React.FunctionComponent<CreateOrderSidebarProps> = ({
	isOpen,
	setOpen,
	clientId,
	projectId,
	projectPhaseId,
}) => {
	const { user } = useAuth();

	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: orderQueries.byProject(projectId).queryKey });
		queryClient.invalidateQueries({ queryKey: USERS_ACTIVE_STAFFINGS_QUERY_KEY(user!.id) });
		setOpen(false);
	}, [projectId, queryClient, setOpen, user]);

	const { data: projectBillingSettingsData, isLoading: projectBillingSettingsIsLoading } = useQuery(
		billingSettingsQueries.byProject(projectId),
	);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={() => setOpen(!isOpen)}>
			{isOpen && projectBillingSettingsIsLoading && <SidebarBusyOverlay />}
			{isOpen && projectBillingSettingsData &&
				<CreateOrderForm
					clientId={clientId}
					projectBillingSettings={projectBillingSettingsData}
					projectId={projectId}
					projectPhaseId={projectPhaseId}
					onSuccess={handleSuccess}
					onCancel={() => setOpen(false)}
				/>}
		</Sidebar>
	);
};

export default CreateOrderSidebar;
